"use client";

import { useEffect, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import QRCode from "qrcode";
import gsap from "gsap";
import { erc20Abi, parseUnits, type Hex } from "viem";
import { useAccount, usePublicClient, useSwitchChain, useWriteContract } from "wagmi";
import { useConnection, useWallet as useSolanaWallet } from "@solana/wallet-adapter-react";
import { PublicKey, Transaction } from "@solana/web3.js";
import {
  createTransferInstruction,
  getAssociatedTokenAddress,
  createAssociatedTokenAccountInstruction,
} from "@solana/spl-token";
import {
  EVM_CHAIN_IDS,
  fetchDepositAddresses,
  fetchDeposits,
  type DepositChain,
  type DepositTokenMeta,
} from "@/lib/deposits-api";
import { useSession } from "@/lib/useSignIn";

// Deposit flow. Primary path: ONE-CLICK, the connected wallet signs a transfer straight to
// the user's own deposit address, so the address itself is invisible plumbing. Fallback path:
// show the address + QR, because a large share of real deposits come FROM exchanges (Binance
// withdrawals etc.), where the user pastes an address into the exchange, and no wallet popup
// can help. Both paths land on the same custody rails; the ledger credits after confirmation.

type Phase =
  | { step: "idle" }
  | { step: "switching" }
  | { step: "sending" }
  | { step: "sent"; hash: string }
  | { step: "error"; message: string };

const CHAIN_LABEL: Record<DepositChain, string> = {
  POLYGON: "Polygon",
  BASE: "Base",
  OPTIMISM: "Optimism",
  BSC: "BNB Chain",
  ETHEREUM: "Ethereum",
  SOLANA: "Solana",
  APTOS: "Aptos",
};

const sel =
  "rounded-md border border-line bg-page px-2 py-2 text-sm text-fg outline-none focus:border-brand";

const DepositModal = ({ open, onClose }: { open: boolean; onClose: () => void }) => {
  const panelRef = useRef<HTMLDivElement>(null);
  const qc = useQueryClient();
  const { isSignedIn } = useSession();

  const [chain, setChain] = useState<DepositChain>("POLYGON");
  const [token, setToken] = useState<"USDT" | "USDC">("USDT");
  const [amount, setAmount] = useState("");
  const [phase, setPhase] = useState<Phase>({ step: "idle" });
  const [showAddress, setShowAddress] = useState(false);
  const [copied, setCopied] = useState(false);

  const { address: evmAddress, chainId: connectedChainId } = useAccount();
  const { switchChainAsync } = useSwitchChain();
  const { writeContractAsync } = useWriteContract();
  const publicClient = usePublicClient();
  const solana = useSolanaWallet();
  const { connection } = useConnection();

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["deposit-addresses"],
    queryFn: fetchDepositAddresses,
    enabled: open && isSignedIn,
    staleTime: 5 * 60_000,
    retry: 1,
  });

  const { data: history } = useQuery({
    queryKey: ["deposits"],
    queryFn: fetchDeposits,
    enabled: open && isSignedIn,
    // While the modal is open poll for the deposit landing, confirmation takes minutes.
    refetchInterval: 20_000,
  });

  const entry = data?.addresses.find((a) => a.chain === chain);
  const tokenMeta: DepositTokenMeta | undefined = entry?.tokens.find((t) => t.token === token);
  const isEvm = chain in EVM_CHAIN_IDS;
  const walletReady = isEvm ? !!evmAddress : chain === "SOLANA" ? !!solana.publicKey : false;
  const oneClickAvailable = walletReady && !!tokenMeta && !!entry;

  useEffect(() => {
    if (!open || !panelRef.current) return;
    const mm = gsap.matchMedia();
    mm.add("(prefers-reduced-motion: no-preference)", () => {
      gsap.from(panelRef.current, { y: 12, opacity: 0, duration: 0.25, ease: "power2.out" });
    });
    return () => mm.revert();
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  // QR is derived async data, react-query owns it (cached per address, no manual state).
  const { data: qr } = useQuery({
    queryKey: ["deposit-qr", entry?.address],
    queryFn: () => QRCode.toDataURL(entry!.address, { margin: 1, width: 168 }),
    enabled: !!entry?.address,
    staleTime: Infinity,
  });

  if (!open) return null;

  const send = async () => {
    if (!entry || !tokenMeta) return;
    const amt = Number(amount);
    if (!Number.isFinite(amt) || amt <= 0) {
      setPhase({ step: "error", message: "Enter a valid amount" });
      return;
    }
    try {
      if (isEvm) {
        const wantChainId = EVM_CHAIN_IDS[chain]!;
        if (connectedChainId !== wantChainId) {
          setPhase({ step: "switching" });
          await switchChainAsync({ chainId: wantChainId });
        }
        setPhase({ step: "sending" });
        const hash = await writeContractAsync({
          address: tokenMeta.contract as Hex,
          abi: erc20Abi,
          functionName: "transfer",
          args: [entry.address as Hex, parseUnits(amount as `${number}`, tokenMeta.decimals)],
        });
        setPhase({ step: "sent", hash });
        await publicClient?.waitForTransactionReceipt({ hash }).catch(() => {});
      } else if (chain === "SOLANA") {
        if (!solana.publicKey || !solana.sendTransaction) throw new Error("Connect a Solana wallet");
        setPhase({ step: "sending" });
        const mint = new PublicKey(tokenMeta.contract);
        const owner = solana.publicKey;
        const dest = new PublicKey(entry.address);
        const fromAta = await getAssociatedTokenAddress(mint, owner);
        const toAta = await getAssociatedTokenAddress(mint, dest);
        const ix = [];
        // Destination ATA may not exist yet for a fresh deposit address, create it (payer:
        // the depositor; rent is trivial) or the transfer fails opaquely.
        if (!(await connection.getAccountInfo(toAta))) {
          ix.push(createAssociatedTokenAccountInstruction(owner, toAta, dest, mint));
        }
        ix.push(
          createTransferInstruction(
            fromAta,
            toAta,
            owner,
            BigInt(parseUnits(amount as `${number}`, tokenMeta.decimals))
          )
        );
        const txn = new Transaction().add(...ix);
        const sig = await solana.sendTransaction(txn, connection);
        setPhase({ step: "sent", hash: sig });
      }
      void qc.invalidateQueries({ queryKey: ["deposits"] });
    } catch (e) {
      const msg = (e as Error).message ?? "";
      setPhase(
        /reject|denied|cancel/i.test(msg)
          ? { step: "idle" }
          : { step: "error", message: msg.slice(0, 140) }
      );
    }
  };

  const copy = async () => {
    if (!entry) return;
    await navigator.clipboard.writeText(entry.address).catch(() => {});
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  const recent = (history?.deposits ?? []).slice(0, 3);

  return (
    <div
      className="fixed inset-0 z-50 grid place-items-center bg-black/60 p-4"
      role="dialog"
      aria-modal="true"
      aria-label="Deposit"
      onClick={onClose}
    >
      <div
        ref={panelRef}
        onClick={(e) => e.stopPropagation()}
        className="max-h-[90dvh] w-full max-w-sm overflow-y-auto rounded-lg border border-line bg-surface p-4 shadow-2xl"
      >
        <div className="mb-3 flex items-center justify-between">
          <h2 className="text-sm font-semibold text-fg">Deposit {token}</h2>
          <button type="button" onClick={onClose} aria-label="Close" className="text-fg-muted hover:text-fg">
            ✕
          </button>
        </div>

        {!isSignedIn && <p className="text-xs text-fg-muted">Connect and sign in first.</p>}
        {isLoading && <p className="text-xs text-fg-muted">Loading deposit rails…</p>}
        {isError && <p className="text-xs text-sell">{(error as Error).message}</p>}

        {entry && (
          <div className="flex flex-col gap-3">
            <div className="grid grid-cols-2 gap-3">
              <label className="flex flex-col gap-1">
                <span className="text-[11px] text-fg-muted">Network</span>
                <select
                  value={chain}
                  onChange={(e) => {
                    setChain(e.target.value as DepositChain);
                    setPhase({ step: "idle" });
                  }}
                  className={sel}
                >
                  {data!.addresses.map((a) => (
                    <option key={a.chain} value={a.chain}>{CHAIN_LABEL[a.chain]}</option>
                  ))}
                </select>
              </label>
              <label className="flex flex-col gap-1">
                <span className="text-[11px] text-fg-muted">Token</span>
                <select value={token} onChange={(e) => setToken(e.target.value as "USDT" | "USDC")} className={sel}>
                  <option value="USDT">USDT</option>
                  <option value="USDC">USDC</option>
                </select>
              </label>
            </div>

            {/* ── Primary: one-click from the connected wallet ─────────────── */}
            {oneClickAvailable ? (
              <>
                <label className="flex flex-col gap-1">
                  <span className="text-[11px] text-fg-muted">Amount ({token})</span>
                  <input
                    inputMode="decimal"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value.replace(/[^\d.]/g, ""))}
                    placeholder="0.00"
                    className={`tnum ${sel}`}
                  />
                </label>
                <button
                  type="button"
                  onClick={() => void send()}
                  disabled={Number(amount) <= 0 || phase.step === "switching" || phase.step === "sending"}
                  className="rounded-md bg-brand py-2.5 text-sm font-medium text-black transition-colors hover:bg-brand-glow disabled:opacity-40"
                >
                  {phase.step === "switching" && "Switching network…"}
                  {phase.step === "sending" && "Confirm in wallet…"}
                  {(phase.step === "idle" || phase.step === "error" || phase.step === "sent") &&
                    `Deposit ${amount || ""} ${token} from wallet`}
                </button>
                {phase.step === "sent" && (
                  <p className="text-xs text-buy">
                    Sent ✓. Your balance credits after on-chain confirmation (usually a few
                    minutes). tx {phase.hash.slice(0, 10)}…
                  </p>
                )}
                {phase.step === "error" && <p className="text-xs text-sell">{phase.message}</p>}
              </>
            ) : (
              <p className="text-xs text-fg-muted">
                {chain === "APTOS"
                  ? "One-click deposits for Aptos are coming, use the address below."
                  : `Connect a ${isEvm ? "browser (EVM)" : "Solana"} wallet for one-click deposits, or use the address below.`}
              </p>
            )}

            {/* ── Fallback: address + QR (e.g. withdrawing from an exchange) ── */}
            <button
              type="button"
              onClick={() => setShowAddress((v) => !v)}
              className="text-left text-[11px] text-fg-muted underline-offset-2 hover:text-fg hover:underline"
            >
              {showAddress ? "Hide address" : "Depositing from an exchange? Show address"}
            </button>
            {showAddress && (
              <div className="flex flex-col items-center gap-2 rounded-md bg-raised p-3">
                {/* eslint-disable-next-line @next/next/no-img-element -- data URI QR */}
                {qr && <img src={qr} alt={`Deposit address QR for ${CHAIN_LABEL[chain]}`} className="rounded" />}
                <code className="tnum break-all text-center text-[11px] text-fg-secondary">{entry.address}</code>
                <button
                  type="button"
                  onClick={() => void copy()}
                  className="rounded bg-page px-3 py-1.5 text-xs text-fg hover:bg-line"
                >
                  {copied ? "Copied ✓" : "Copy address"}
                </button>
                <p className="text-center text-[10px] text-fg-muted">
                  Send only {entry.tokens.length ? entry.tokens.map((t) => t.token).join(" or ") : "USDT/USDC"} on{" "}
                  {CHAIN_LABEL[chain]}. Other assets or networks will be lost.
                </p>
              </div>
            )}

            {recent.length > 0 && (
              <div className="border-t border-line pt-2">
                <p className="mb-1 text-[11px] uppercase tracking-wide text-fg-muted">Recent</p>
                {recent.map((d) => (
                  <div key={d.id} className="flex justify-between py--0.5 text-[11px]">
                    <span className="text-fg-secondary">
                      {d.amount} {d.token} · {CHAIN_LABEL[d.chain]}
                    </span>
                    <span className={d.status === "completed" ? "text-buy" : "text-fg-muted"}>
                      {d.status === "completed" ? "credited ✓" : `${d.status} (${d.confirmations} conf)`}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default DepositModal;
